import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Upload, Sparkles, X, Loader2, Image as ImageIcon, 
  Trash2, Eye, Download, TrendingUp, CheckCircle, Video, 
  Facebook, Send, Play, Pause, BarChart3, RefreshCw, Zap, Check, AlertCircle, Type
} from 'lucide-react';
import { API_URL } from '../../config';
import QuotaStats from './QuotaStats';
import CreditsPurchase from './CreditsPurchase';

const PRODUCT_CATEGORIES = [
  { value: 'top', label: '👕 Haut / T-shirt / Chemise', fashn: 'tops' },
  { value: 'jacket', label: '🧥 Veste / Manteau', fashn: 'tops' },
  { value: 'bottom', label: '👖 Pantalon / Short', fashn: 'bottoms' },
  { value: 'skirt', label: '👗 Jupe / Robe', fashn: 'one-pieces' },
  { value: 'shoes', label: '👟 Chaussures', fashn: 'bottoms' },
  { value: 'accessories', label: '👜 Accessoires', fashn: 'tops' }
];

export default function StudioPro({ clientId, onBack }) {
  const [view, setView] = useState('manage');
  
  // Data states
  const [models, setModels] = useState([]);
  const [products, setProducts] = useState([]);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedVideosForMerge, setSelectedVideosForMerge] = useState([]);
  const [videos, setVideos] = useState([]);
  const [stats, setStats] = useState({ 
    total: 0, 
    published: 0, 
    draft: 0,
    totalVideos: 0,
    publishedVideos: 0 
  });
  const [quotas, setQuotas] = useState({
    max_images: 0,
    used_images: 0,
    remaining_images: 0,
    max_videos_wan: 0,
    max_videos_hailua: 0,
    used_videos_wan: 0,
    used_videos_hailua: 0,
    remaining_videos_wan: 0,
    remaining_videos_hailua: 0
  });
  
  // Upload states
  const [uploading, setUploading] = useState(false);
  
  // Generation states
  const [generating, setGenerating] = useState(false);
  const [generatingVideos, setGeneratingVideos] = useState(false);
  const [category, setCategory] = useState(PRODUCT_CATEGORIES[0]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [videoWatermarkEnabled, setVideoWatermarkEnabled] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [videoWatermarkText, setVideoWatermarkText] = useState('');
  const [additionalPrompt, setAdditionalPrompt] = useState('');
const [additionalPromptEnabled, setAdditionalPromptEnabled] = useState(false);

const [limits, setLimits] = useState({
  models: { current: 0, limit: 10, remaining: 10, can_upload: true },
  products: { current: 0, limit: 25, remaining: 25, can_upload: true },
  images: { current: 0, limit: 100, remaining: 100, can_generate: true },
  videos: { current: 0, limit: 50, remaining: 50, can_generate: true }  
});

  // Modal states
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showPublishModal, setShowPublishModal] = useState(null);
  const [publishCaption, setPublishCaption] = useState('');
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedAIModel, setSelectedAIModel] = useState('wan');
  const [watermarkSettings, setWatermarkSettings] = useState({
    enabled: false,
    type: 'text', // 'logo' ou 'text'
    text: '',
    opacity: 0.3, // 0.0 à 1.0
    logoFile: null,
    logoPreview: null
  });
  
  // 🆕 États pour sélection multiple et publication
  const [selectedImagesForPublish, setSelectedImagesForPublish] = useState([]);
  const [publishMode, setPublishMode] = useState('auto');
  const [isPublishing, setIsPublishing] = useState(false);

  // 🆕 États pour sélection multiple vidéos
  const [selectedImagesForVideo, setSelectedImagesForVideo] = useState([]);

  // 🆕 Modal personnalisée pour remplacer alert
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });

  const showAlert = (message, title = 'Information', type = 'info') => {
    setAlertModal({ show: true, title, message, type });
  };

  const closeAlert = () => {
    setAlertModal({ show: false, title: '', message: '', type: 'info' });
  };

  useEffect(() => {
    loadData();
    loadQuotas();
    loadLimits();
    
    const interval = setInterval(() => {
      loadQuotas();
      loadLimits();
    }, 30000);
    return () => clearInterval(interval);
  }, [clientId]);
  
  const loadLimits = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}/marketing/limits?t=${Date.now()}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          // ❌ SUPPRIME LE CACHE-CONTROL QUI CAUSE LE BLOCAGE CORS
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log("🔥 LIMITES REÇUES DU SERVEUR :", data.limits);
        
        // ✅ LOGS DÉTAILLÉS
        console.log("📊 DÉTAIL IMAGES:", {
          current: data.limits.images.current,
          limit: data.limits.images.limit,
          can_generate: data.limits.images.can_generate,
          bouton_devrait_etre: data.limits.images.can_generate ? "ACTIF" : "GRISÉ"
        });
        
        console.log("📊 DÉTAIL VIDÉOS:", {
          current: data.limits.videos.current,
          limit: data.limits.videos.limit,
          can_generate: data.limits.videos.can_generate,
          bouton_devrait_etre: data.limits.videos.can_generate ? "ACTIF" : "GRISÉ"
        });
        
        setLimits(data.limits);
      } else {
        console.error("❌ Erreur API /limits:", response.status, await response.text());
      }
    } catch (error) {
      console.error('❌ Erreur loadLimits:', error);
    }
  };

  const loadQuotas = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}/quotas?service=studiopro`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setQuotas(data);
      }
    } catch (error) {
      console.error('Error loading quotas:', error);
    }
  };

  const uploadWatermarkLogo = async (file) => {
    const formData = new FormData();
    formData.append('logo', file);
  
    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}/marketing/watermark-logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });
  
      if (response.ok) {
        const data = await response.json();
        showAlert('Logo uploadé avec succès !', 'Succès', 'success');
        return data.logo_url;
      } else {
        const error = await response.json();
        showAlert(error.message, 'Erreur', 'error');
        return null;
      }
    } catch (error) {
      showAlert('Erreur d\'upload', 'Erreur', 'error');
      return null;
    }
  };

  const QuotasHeader = () => {
    // Utiliser directement les valeurs de l'API
    const imagesRemaining = quotas.remaining_images || 0;
    const videosWanRemaining = quotas.remaining_videos_wan || 0;
    const videosHailuaRemaining = quotas.remaining_videos_hailua || 0;
  
    const getColorClass = (used, max) => {
      if (max === 0) return 'from-gray-500 to-gray-600';
      const percentage = (used / max) * 100;
      if (percentage >= 90) return 'from-red-500 to-red-600';
      if (percentage >= 70) return 'from-orange-500 to-orange-600';
      return 'from-green-500 to-green-600';
    };
  
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Images Studio */}
        <div className={`bg-gradient-to-r ${getColorClass(quotas.used_images, quotas.max_images)} rounded-xl p-4 shadow-lg`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-white" />
              <span className="text-white font-bold text-sm">Images Studio</span>
            </div>
            {imagesRemaining <= 5 && imagesRemaining > 0 && (
              <AlertCircle className="w-4 h-4 text-white animate-pulse" />
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-white/90 text-xs">Générées</span>
              <span className="text-white font-bold text-lg">{quotas.used_images}</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white transition-all duration-500"
                style={{ width: `${quotas.max_images > 0 ? (quotas.used_images / quotas.max_images) * 100 : 0}%` }}
              />
            </div>
            <div className="text-white/80 text-xs text-center">
              {imagesRemaining} / {quotas.max_images} restantes
            </div>
          </div>
        </div>
  
        {/* Vidéos Wan V12 */}
        <div className={`bg-gradient-to-r ${getColorClass(quotas.used_videos_wan || 0, quotas.max_videos_wan)} rounded-xl p-4 shadow-lg`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-white" />
              <span className="text-white font-bold text-sm">TeckiA V12</span>
            </div>
            {videosWanRemaining <= 5 && videosWanRemaining > 0 && (
              <AlertCircle className="w-4 h-4 text-white animate-pulse" />
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-white/90 text-xs">Générées</span>
              <span className="text-white font-bold text-lg">{quotas.used_videos_wan || 0}</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white transition-all duration-500"
                style={{ width: `${quotas.max_videos_wan > 0 ? ((quotas.used_videos_wan || 0) / quotas.max_videos_wan) * 100 : 0}%` }}
              />
            </div>
            <div className="text-white/80 text-xs text-center">
              {videosWanRemaining} / {quotas.max_videos_wan} restantes
            </div>
          </div>
        </div>
  
        {/* Vidéos Hailua V15 */}
        <div className={`bg-gradient-to-r ${getColorClass(quotas.used_videos_hailua || 0, quotas.max_videos_hailua)} rounded-xl p-4 shadow-lg`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-white" />
              <span className="text-white font-bold text-sm">TeckiA V15</span>
            </div>
            {videosHailuaRemaining <= 5 && videosHailuaRemaining > 0 && (
              <AlertCircle className="w-4 h-4 text-white animate-pulse" />
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-white/90 text-xs">Générées</span>
              <span className="text-white font-bold text-lg">{quotas.used_videos_hailua || 0}</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white transition-all duration-500"
                style={{ width: `${quotas.max_videos_hailua > 0 ? ((quotas.used_videos_hailua || 0) / quotas.max_videos_hailua) * 100 : 0}%` }}
              />
            </div>
            <div className="text-white/80 text-xs text-center">
              {videosHailuaRemaining} / {quotas.max_videos_hailua} restantes
            </div>
          </div>
        </div>
      </div>
    );
  };

  const loadData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const [clientRes, modelsRes, productsRes, generatedRes, videosRes] = await Promise.all([
        fetch(`${API_URL}/api/clients/${clientId}`, { headers }),
        fetch(`${API_URL}/api/clients/${clientId}/marketing/models`, { headers }),
        fetch(`${API_URL}/api/clients/${clientId}/marketing/products`, { headers }),
        fetch(`${API_URL}/api/clients/${clientId}/marketing/generated`, { headers }),
        fetch(`${API_URL}/api/clients/${clientId}/marketing/videos`, { headers })
      ]);
  
      if (clientRes.ok) {
        const data = await clientRes.json();
        setClientData(data.client);
      }
  
      if (modelsRes.ok) {
        const data = await modelsRes.json();
        setModels(data.models || []);
      }
  
      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data.products || []);
      }
  
      if (generatedRes.ok) {
        const data = await generatedRes.json();
        
        // ✅ STOCKE LES VRAIES INFOS
        setGeneratedImages(data.images || []);
        
        const total = data.total_count || 0;  // ✅ TOTAL RÉEL
        const displayed = data.displayed_count || 0;  // ✅ AFFICHÉ
        const published = (data.images || []).filter(img => img.status === 'published').length;
        const draft = (data.images || []).filter(img => img.status === 'draft').length;
        
        setStats(prev => ({ 
          ...prev, 
          total,  // ✅ TOTAL RÉEL (ex: 406)
          displayed,  // ✅ AFFICHÉ (max 100)
          published, 
          draft,
          limit_exceeded: data.limit_exceeded  // ✅ FLAG
        }));
      }
  
      if (videosRes.ok) {
        const data = await videosRes.json();
        
        setVideos(data.videos || []);
        
        const totalVideos = data.total_count || 0;
        const displayedVideos = data.displayed_count || 0;
        const publishedVideos = (data.videos || []).filter(v => v.status === 'published').length;
        
        setStats(prev => ({ 
          ...prev, 
          totalVideos,
          displayedVideos,
          publishedVideos,
          videos_limit_exceeded: data.limit_exceeded
        }));
      }
    } catch (error) {
      console.error('💥 Error loading data:', error);
    }
  };
  
  const handleModelUpload = async (e) => {
  const files = Array.from(e.target.files);
  if (files.length === 0) return;

  // ✅ VÉRIFIE LA LIMITE
  if (!limits.models.can_upload) {
    showAlert(
      `Limite atteinte (${limits.models.limit} modèles max).\n\nSupprimez des modèles pour en ajouter.`,
      'Limite atteinte',
      'warning'
    );
    e.target.value = '';
    return;
  }

  // ✅ VÉRIFIE SI ON DÉPASSE
  if (files.length > limits.models.remaining) {
    showAlert(
      `Vous ne pouvez ajouter que ${limits.models.remaining} modèle(s) supplémentaire(s).`,
      'Quota insuffisant',
      'warning'
    );
    e.target.value = '';
    return;
  }

  setUploading(true);

  for (const file of files) {
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}/marketing/models`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
        body: formData
      });

      if (response.ok) {
        await loadData();
        await loadLimits();
      } else {
        const error = await response.json();
        showAlert(error.message, 'Erreur', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
    }
  }

  setUploading(false);
  e.target.value = '';
};
  
const handleProductUpload = async (e) => {
  const files = Array.from(e.target.files);
  if (files.length === 0) return;

  // ✅ VÉRIFIE LA LIMITE
  if (!limits.products.can_upload) {
    showAlert(
      `Limite atteinte (${limits.products.limit} produits max).\n\nSupprimez des produits pour en ajouter.`,
      'Limite atteinte',
      'warning'
    );
    e.target.value = '';
    return;
  }

  // ✅ VÉRIFIE SI ON DÉPASSE
  if (files.length > limits.products.remaining) {
    showAlert(
      `Vous ne pouvez ajouter que ${limits.products.remaining} produit(s) supplémentaire(s).`,
      'Quota insuffisant',
      'warning'
    );
    e.target.value = '';
    return;
  }

  setUploading(true);

  for (const file of files) {
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}/marketing/products`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
        body: formData
      });

      if (response.ok) {
        await loadData();
        await loadLimits();
      } else {
        const error = await response.json();
        showAlert(error.message, 'Erreur', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
    }
  }

  setUploading(false);
  e.target.value = '';
};

const generateBatch = async () => {
  // ✅ VÉRIFICATION COHÉRENTE avec le disabled du bouton
  console.log("🔍 CHECK LIMITE IMAGES:", {
    current: limits.images.current,
    limit: limits.images.limit,
    can_generate: limits.images.can_generate
  });

  if (!limits.images.can_generate) {
    showAlert(
      `❌ Stockage d'images plein !\n\n` +
      `Vous avez ${limits.images.current} images sur un maximum de ${limits.images.limit}.\n\n` +
      `💡 Supprimez des images pour continuer à générer.`,
      'Stockage plein',
      'error'
    );
    return;
  }

  if (!selectedModel) {
    showAlert('Veuillez sélectionner un modèle', 'Attention', 'warning');
    return;
  }

  if (selectedProducts.length === 0) {
    showAlert('Veuillez sélectionner au moins 1 produit', 'Attention', 'warning');
    return;
  }

  // ✅ VÉRIFICATION SI ON VA DÉPASSER LE STOCKAGE
  if (limits.images.current + selectedProducts.length > limits.images.limit) {
    showAlert(
      `⚠️ Génération impossible !\n\n` +
      `Vous voulez générer ${selectedProducts.length} image(s)\n` +
      `Vous avez déjà ${limits.images.current} / ${limits.images.limit} images\n\n` +
      `Si vous générez, vous aurez ${limits.images.current + selectedProducts.length} images (DÉPASSEMENT).\n\n` +
      `💡 Supprimez au moins ${(limits.images.current + selectedProducts.length) - limits.images.limit} image(s) avant.`,
      'Stockage insuffisant',
      'warning'
    );
    return;
  }

  // VÉRIFICATION DU QUOTA API (Fashn)
  if (quotas.remaining_images <= 0) {
    showAlert(
      '❌ Quota API épuisé !\n\n' +
      'Vous avez atteint la limite de génération d\'images API.\n\n' +
      '💡 Pour continuer à générer des images :\n' +
      '• Contactez le service client\n' +
      '• Souscrivez à des jetons supplémentaires\n\n' +
      '📧 Support : support@tdidigital.io',
      'Quota API épuisé',
      'error'
    );
    return;
  }

  if (selectedProducts.length > quotas.remaining_images) {
    showAlert(
      `⚠️ Quota API insuffisant !\n\n` +
      `Vous avez sélectionné ${selectedProducts.length} produit(s)\n` +
      `Mais il vous reste seulement ${quotas.remaining_images} crédit(s) API disponible(s).\n\n` +
      `Réduisez votre sélection ou contactez le service client.`,
      'Quota API insuffisant',
      'warning'
    );
    return;
  }

  const total = selectedProducts.length;
  setGenerating(true);
  setProgress({ current: 0, total });

  try {
    const response = await fetch(`${API_URL}/api/clients/${clientId}/marketing/generate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model_id: selectedModel.$id,
        product_ids: selectedProducts.map(p => p.$id),
        category: category.fashn,
        watermark_enabled: watermarkSettings.enabled,
        watermark_type: watermarkSettings.type,
        watermark_text: watermarkSettings.text,
        watermark_opacity: watermarkSettings.opacity,
        additional_prompt_enabled: additionalPromptEnabled,
        additional_prompt: additionalPrompt
      })
    });

    if (!response.ok) {
      const error = await response.json();
      showAlert(error.message, 'Erreur', 'error');
      setGenerating(false);
      return;
    }

    const data = await response.json();
    const taskId = data.task_id;

    console.log('✅ Génération lancée:', taskId);

    const pollInterval = setInterval(async () => {
      try {
        const statusRes = await fetch(
          `${API_URL}/api/clients/${clientId}/marketing/generate/status/${taskId}`,
          {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
          }
        );

        if (statusRes.ok) {
          const statusData = await statusRes.json();

          console.log('📊 Progression:', statusData.completed, '/', statusData.total);

          setProgress({
            current: statusData.completed,
            total: statusData.total
          });

          if (statusData.status === 'completed') {
            clearInterval(pollInterval);
            setGenerating(false);
            
            showAlert(
              `✅ ${statusData.completed} images générées avec succès !${
                statusData.failed > 0 ? `\n⚠️ ${statusData.failed} échec(s)` : ''
              }`,
              'Génération terminée',
              'success'
            );

            await loadData();
            await loadQuotas();
            await loadLimits(); // ✅ RECHARGE LES LIMITES
            setView('generated');
            setSelectedModel(null);
            setSelectedProducts([]);
            setAdditionalPrompt('');
            setAdditionalPromptEnabled(false);
          }

          if (statusData.status === 'failed') {
            clearInterval(pollInterval);
            setGenerating(false);
            showAlert(statusData.error || 'Erreur de génération', 'Erreur', 'error');
          }
        }
      } catch (error) {
        console.error('❌ Erreur polling:', error);
      }
    }, 2000);

    setTimeout(() => {
      clearInterval(pollInterval);
      if (generating) {
        setGenerating(false);
        showAlert(
          'La génération prend plus de temps que prévu.\n' +
          'Rechargez la page pour voir les images générées.',
          'Timeout',
          'warning'
        );
      }
    }, 20 * 60 * 1000);

  } catch (error) {
    setGenerating(false);
    setProgress({ current: 0, total: 0 });
    showAlert('Erreur de connexion au serveur', 'Erreur réseau', 'error');
    console.error(error);
  }
};

  const toggleProductSelection = (product) => {
    setSelectedProducts(prev => {
      const exists = prev.find(p => p.$id === product.$id);
      if (exists) {
        return prev.filter(p => p.$id !== product.$id);
      } else {
        return [...prev, product];
      }
    });
  };

  const toggleImageSelection = (image) => {
    setSelectedImagesForPublish(prev => {
      const exists = prev.find(img => img.$id === image.$id);
      if (exists) {
        return prev.filter(img => img.$id !== image.$id);
      } else {
        return [...prev, image];
      }
    });
  };

  const publishSelectedVideos = async () => {
    if (selectedVideosForMerge.length === 0) {
      showAlert('Sélectionnez au moins une vidéo', 'Attention', 'warning');
      return;
    }
  
    if (publishMode === 'manual' && !publishCaption.trim()) {
      showAlert('Saisissez un texte', 'Attention', 'warning');
      return;
    }
  
    setIsPublishing(true);
  
    try {
      if (publishMode === 'auto') {
        // Publication automatique avec TeckiA
        const response = await fetch(`${API_URL}/api/clients/${clientId}/marketing/videos/auto-publish`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            video_ids: selectedVideosForMerge.map(v => v.$id)
          })
        });
  
        if (response.ok) {
          const data = await response.json();
          showAlert(`Vidéo(s) publiée(s) !\n\n${data.caption}`, 'Succès', 'success');
          await loadData();
          setSelectedVideosForMerge([]);
        } else {
          const error = await response.json();
          showAlert(error.message, 'Erreur', 'error');
        }
      } else {
        // Publication manuelle
        let successCount = 0;
  
        for (const video of selectedVideosForMerge) {
          const response = await fetch(`${API_URL}/api/clients/${clientId}/marketing/videos/${video.$id}/publish`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              caption: publishCaption
            })
          });
  
          if (response.ok) {
            successCount++;
          }
        }
  
        if (successCount > 0) {
          showAlert(`${successCount} vidéo(s) publiée(s) !`, 'Succès', 'success');
          await loadData();
          setSelectedVideosForMerge([]);
          setPublishCaption('');
        }
      }
    } catch (error) {
      showAlert('Erreur de connexion', 'Erreur réseau', 'error');
      console.error(error);
    } finally {
      setIsPublishing(false);
    }
  };
  
  const mergeVideos = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}/marketing/videos/merge`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          video_ids: selectedVideosForMerge.map(v => v.$id)
        })
      });
  
      if (response.ok) {
        showAlert('Vidéos fusionnées !', 'Succès', 'success');
        await loadData();
        setSelectedVideosForMerge([]);
      } else {
        const error = await response.json();
        showAlert(error.message, 'Erreur', 'error');
      }
    } catch (error) {
      showAlert('Erreur de connexion', 'Erreur réseau', 'error');
    }
  };

  // 🆕 Toggle sélection pour vidéos
  const toggleImageForVideo = (image) => {
    setSelectedImagesForVideo(prev => {
      const exists = prev.find(img => img.$id === image.$id);
      if (exists) {
        return prev.filter(img => img.$id !== image.$id);
      } else {
        return [...prev, image];
      }
    });
  };

  // 🆕 Générer vidéos à partir des images sélectionnées
  const generateVideosFromSelected = async () => {
    if (selectedImagesForVideo.length === 0) {
      showAlert('Veuillez sélectionner au moins une image', 'Attention', 'warning');
      return;
    }
  
    // ✅ VÉRIFICATION COHÉRENTE
    console.log("🔍 CHECK LIMITE VIDÉOS:", {
      current: limits.videos.current,
      limit: limits.videos.limit,
      can_generate: limits.videos.can_generate
    });
  
    if (!limits.videos.can_generate) {
      showAlert(
        `❌ Stockage de vidéos plein !\n\n` +
        `Vous avez ${limits.videos.current} vidéos sur un maximum de ${limits.videos.limit}.\n\n` +
        `💡 Supprimez des vidéos pour continuer à générer.`,
        'Stockage plein',
        'error'
      );
      return;
    }
  
    // ✅ VÉRIFICATION SI ON VA DÉPASSER LE STOCKAGE
    if (limits.videos.current + selectedImagesForVideo.length > limits.videos.limit) {
      showAlert(
        `⚠️ Génération impossible !\n\n` +
        `Vous voulez générer ${selectedImagesForVideo.length} vidéo(s)\n` +
        `Vous avez déjà ${limits.videos.current} / ${limits.videos.limit} vidéos\n\n` +
        `Si vous générez, vous aurez ${limits.videos.current + selectedImagesForVideo.length} vidéos (DÉPASSEMENT).\n\n` +
        `💡 Supprimez au moins ${(limits.videos.current + selectedImagesForVideo.length) - limits.videos.limit} vidéo(s) avant.`,
        'Stockage insuffisant',
        'warning'
      );
      return;
    }
  
    // Validation watermark
    if (videoWatermarkEnabled && !videoWatermarkText.trim()) {
      showAlert(
        'Veuillez saisir un texte pour le watermark ou décochez l\'option.',
        'Texte manquant',
        'warning'
      );
      return;
    }
  
    // ✅ VÉRIFICATION DU QUOTA API (Runway)
    const quotaField = selectedAIModel === 'wan' ? 'remaining_videos_wan' : 'remaining_videos_hailua';
    const modelName = selectedAIModel === 'wan' ? 'TeckiA V12' : 'TeckiA V15P';
    
    if (quotas[quotaField] <= 0) {
      showAlert(
        `❌ Quota API ${modelName} épuisé !\n\n` +
        'Vous avez atteint la limite de génération de vidéos API.\n\n' +
        '💡 Pour continuer à générer des vidéos :\n' +
        '• Contactez le service client\n' +
        '• Souscrivez à des jetons supplémentaires\n\n' +
        '📧 Support : support@tdidigital.io',
        'Quota API épuisé',
        'error'
      );
      return;
    }
  
    if (selectedImagesForVideo.length > quotas[quotaField]) {
      showAlert(
        `⚠️ Quota API ${modelName} insuffisant !\n\n` +
        `Vous avez sélectionné ${selectedImagesForVideo.length} image(s)\n` +
        `Mais il vous reste seulement ${quotas[quotaField]} crédit(s) API ${modelName} disponible(s).\n\n` +
        `Réduisez votre sélection ou contactez le service client.`,
        'Quota API insuffisant',
        'warning'
      );
      return;
    }
  
    setGeneratingVideos(true);
  
    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}/marketing/generate-videos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          image_ids: selectedImagesForVideo.map(img => img.$id),
          ai_model: selectedAIModel,
          video_watermark_enabled: videoWatermarkEnabled, 
          video_watermark_text: videoWatermarkText.trim()  
        })
      });
  
      if (response.ok) {
        const data = await response.json();
        showAlert(`${data.video_count} vidéo(s) générée(s) avec succès !`, 'Succès', 'success');
        await loadData();
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await loadQuotas();
        await loadLimits(); // ✅ RECHARGE LES LIMITES
        setSelectedImagesForVideo([]);
        setVideoWatermarkEnabled(false);
        setVideoWatermarkText('');
        setView('videos');
      } else {
        const error = await response.json();
        showAlert(error.message, 'Erreur', 'error');
      }
    } catch (error) {
      showAlert('Erreur de connexion au serveur', 'Erreur réseau', 'error');
      console.error(error);
    } finally {
      setGeneratingVideos(false);
    }
  };

  const publishSelectedImages = async () => {
    if (selectedImagesForPublish.length === 0) {
      showAlert('Veuillez sélectionner au moins une image', 'Attention', 'warning');
      return;
    }

    if (publishMode === 'manual' && !publishCaption.trim()) {
      showAlert('Veuillez saisir un texte de publication', 'Attention', 'warning');
      return;
    }

    setIsPublishing(true);

    try {
      if (publishMode === 'auto') {
        const response = await fetch(`${API_URL}/api/clients/${clientId}/marketing/auto-publish`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            generated_ids: selectedImagesForPublish.map(img => img.$id)
          })
        });

        if (response.ok) {
          const data = await response.json();
          showAlert(`Publication automatique créée !\n\nTexte généré: ${data.caption}\n\nURL: ${data.post_url}`, 'Succès', 'success');
          await loadData();
          setSelectedImagesForPublish([]);
          setPublishCaption('');
        } else {
          const error = await response.json();
          showAlert(error.message, 'Erreur', 'error');
        }
      } else {
        let successCount = 0;
        
        for (const img of selectedImagesForPublish) {
          const response = await fetch(`${API_URL}/api/clients/${clientId}/marketing/${img.$id}/publish`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              caption: publishCaption
            })
          });

          if (response.ok) {
            successCount++;
          }
        }

        if (successCount > 0) {
          showAlert(`${successCount} image(s) publiée(s) sur Facebook !`, 'Succès', 'success');
          await loadData();
          setSelectedImagesForPublish([]);
          setPublishCaption('');
        }
      }
    } catch (error) {
      showAlert('Erreur de connexion au serveur', 'Erreur réseau', 'error');
      console.error(error);
    } finally {
      setIsPublishing(false);
    }
  };

  const deleteItem = async (type, id) => {
    try {
      const endpoints = {
        'model': `/api/clients/${clientId}/marketing/models/${id}`,
        'product': `/api/clients/${clientId}/marketing/products/${id}`,
        'image': `/api/clients/${clientId}/marketing/generated/${id}`,
        'video': `/api/clients/${clientId}/marketing/videos/${id}`
      };
  
      const response = await fetch(`${API_URL}${endpoints[type]}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
  
      if (response.ok) {
        showAlert('Élément supprimé avec succès', 'Succès', 'success');
        await loadData();
        await loadLimits(); // ✅ RECHARGE LES LIMITES
        setShowDeleteConfirm(null);
      } else {
        const error = await response.json();
        showAlert(error.message || 'Erreur de suppression', 'Erreur', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showAlert('Erreur de connexion au serveur', 'Erreur réseau', 'error');
    }
  };

  const publishToFacebook = async () => {
    if (!showPublishModal) return;

    const { type, id } = showPublishModal;
    const endpoint = type === 'image' 
      ? `/api/clients/${clientId}/marketing/${id}/publish`
      : `/api/clients/${clientId}/marketing/videos/${id}/publish`;

    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          caption: publishCaption || undefined
        })
      });

      if (response.ok) {
        showAlert('Publié sur Facebook avec succès !', 'Succès', 'success');
        await loadData();
        setShowPublishModal(null);
        setPublishCaption('');
      } else {
        const error = await response.json();
        showAlert(error.message, 'Erreur', 'error');
      }
    } catch (error) {
      showAlert('Erreur de connexion au serveur', 'Erreur réseau', 'error');
      console.error(error);
    }
  };

  // ========================================
  // VIEW: MANAGE (Upload + Générer)
  // ========================================
  if (view === 'manage') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 p-4 md:p-8">
        
        {/* Header */}
        <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl mb-8">
          <div className="container mx-auto px-4 py-4 md:py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img 
                  src="/TeckiA-logo.gif" 
                  alt="TeckiA IA" 
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
                />
                <div>
                  <h1 className="text-base md:text-xl font-bold text-white">TeckiA Fashion Pro</h1>
                  <p className="text-xs md:text-sm text-purple-200">TeckiA Fashion Try on</p>
                </div>
              </div>
              <button
                onClick={() => setView('credits')}
                className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20 hover:from-yellow-500/30 hover:to-orange-500/30 text-yellow-300 border border-yellow-500/30 transition-all hover:scale-105 text-sm md:text-base group"
              >
                <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                <span className="hidden sm:inline">Acheter Crédits</span>
                <span className="sm:hidden">Crédits</span>
              </button>
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden md:inline">Retour</span>
              </button>
            </div>
          </div>
        </header>


        <div className="max-w-7xl mx-auto">
          {/* Navigation */}
          <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
            <button
              onClick={() => setView('manage')}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold whitespace-nowrap"
            >
              📤 Gérer
            </button>
            <button
              onClick={() => setView('generated')}
              className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium whitespace-nowrap transition-all"
            >
              📸 Images ({stats.total})
            </button>
            <button
              onClick={() => setView('videos')}
              className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium whitespace-nowrap transition-all"
            >
              🎬 Vidéos ({stats.totalVideos})
            </button>
            <button
              onClick={() => setView('stats')}
              className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium whitespace-nowrap transition-all"
            >
              📊 Stats
            </button>
            
          </div>
          <QuotasHeader />
          

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Upload Modèles */}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                  <Upload className="w-5 h-5 text-white" />
                </div>
                1. Choisissez un Modèle
              </h2>

              {/* ✅ AFFICHE LA LIMITE D'UPLOAD */}
              <div className="mb-4 px-4 py-3 rounded-xl bg-purple-500/10 border border-purple-500/30">
                <div className="flex items-center justify-between">
                  <span className="text-white/80 text-sm">📤 Modèles uploadés</span>
                  <span className={`text-sm font-bold ${limits.models.can_upload ? 'text-green-400' : 'text-red-400'}`}>
                    {limits.models.current} / {limits.models.limit}
                  </span>
                </div>
                {!limits.models.can_upload && (
                  <p className="text-red-400 text-xs mt-2">
                  ⚠️ Limite atteinte - Supprimez des modèles pour en ajouter
                </p>
              )}
            </div>

            <label className={`block ${limits.models.can_upload ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} mb-6`}>
              <div className={`border-2 border-dashed ${limits.models.can_upload ? 'border-purple-500/50 hover:border-purple-500' : 'border-red-500/50'} rounded-2xl p-8 text-center transition-all bg-white/5 hover:bg-white/10`}>
                <Upload className={`w-12 h-12 ${limits.models.can_upload ? 'text-purple-400' : 'text-red-400'} mx-auto mb-3`} />
                <p className="text-white font-medium mb-1">
                  {uploading ? 'Upload en cours...' : limits.models.can_upload ? 'Ajouter modèles' : 'Limite atteinte'}
                </p>
                <p className="text-white/60 text-sm">
                  {limits.models.can_upload ? 'Photos en pied, fond neutre' : `Supprimez des modèles (${limits.models.current}/${limits.models.limit})`}
                </p>
              </div>
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={handleModelUpload}
                disabled={uploading || !limits.models.can_upload}
              />
            </label>

                {models.length > 0 && (
                    <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                    {models.map(model => (
                        <div
                        key={model.$id}
                        onClick={() => setSelectedModel(model)}
                        className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all ${
                            selectedModel?.$id === model.$id
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 scale-105'
                            : 'bg-white/5 hover:bg-white/10'
                        }`}
                        >
                        <img
                            src={`${API_URL}${model.image_url}`}
                            alt="Model"
                            className="w-16 h-16 rounded-lg object-cover"
                        />
                        <div className="flex-1">
                            <p className="text-white font-medium">{model.name || 'Modèle'}</p>
                            <p className="text-white/60 text-sm">{new Date(model.$createdAt).toLocaleDateString()}</p>
                        </div>
                        {selectedModel?.$id === model.$id && (
                            <CheckCircle className="w-6 h-6 text-white" />
                        )}
                        <button
                            onClick={(e) => {
                            e.stopPropagation();
                            setShowDeleteConfirm({ type: 'model', id: model.$id });
                            }}
                            className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition-all"
                        >
                            <Trash2 className="w-5 h-5 text-red-400" />
                        </button>
                        </div>
                    ))}
                    </div>
                )}
            </div>

            {/* Upload Produits */}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center">
                  <Upload className="w-5 h-5 text-white" />
                </div>
                2. Ajoutez vos Produits
              </h2>

              {/* ✅ AFFICHE LA LIMITE */}
              <div className="mb-4 px-4 py-3 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-white/80 text-sm">Produits uploadés</span>
                  <span className={`text-sm font-bold ${limits.products.can_upload ? 'text-green-400' : 'text-red-400'}`}>
                    {limits.products.current} / {limits.products.limit}
                  </span>
                </div>
                {!limits.products.can_upload && (
                  <p className="text-red-400 text-xs mt-2">
                    ⚠️ Limite atteinte - Supprimez des produits pour en ajouter
                  </p>
                )}
              </div>

              <label className={`block ${limits.products.can_upload ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} mb-6`}>
                <div className={`border-2 border-dashed ${limits.products.can_upload ? 'border-cyan-500/50 hover:border-cyan-500' : 'border-red-500/50'} rounded-2xl p-8 text-center transition-all bg-white/5 hover:bg-white/10`}>
                  <Upload className={`w-12 h-12 ${limits.products.can_upload ? 'text-cyan-400' : 'text-red-400'} mx-auto mb-3`} />
                  <p className="text-white font-medium mb-1">
                    {uploading ? 'Upload en cours...' : limits.products.can_upload ? 'Ajouter produits' : 'Limite atteinte'}
                  </p>
                  <p className="text-white/60 text-sm">
                    {limits.products.can_upload ? 'Fond transparent recommandé' : `Supprimez des produits (${limits.products.current}/${limits.products.limit})`}
                  </p>
                </div>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={handleProductUpload}
                  disabled={uploading || !limits.products.can_upload}
                />
              </label>

                {products.length > 0 && (
                    <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                    {products.map(product => (
                        <div
                        key={product.$id}
                        onClick={() => toggleProductSelection(product)}
                        className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all ${
                            selectedProducts.find(p => p.$id === product.$id)
                            ? 'bg-gradient-to-r from-cyan-600 to-blue-600 scale-105'
                            : 'bg-white/5 hover:bg-white/10'
                        }`}
                        >
                        <img
                            src={`${API_URL}${product.image_url}`}
                            alt="Product"
                            className="w-16 h-16 rounded-lg object-cover bg-white/10"
                        />
                        <div className="flex-1">
                            <p className="text-white font-medium">{product.name || 'Produit'}</p>
                            <p className="text-white/60 text-sm">{new Date(product.$createdAt).toLocaleDateString()}</p>
                        </div>
                        {selectedProducts.find(p => p.$id === product.$id) && (
                            <CheckCircle className="w-6 h-6 text-white" />
                        )}
                        <button
                            onClick={(e) => {
                            e.stopPropagation();
                            setShowDeleteConfirm({ type: 'product', id: product.$id });
                            }}
                            className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition-all"
                        >
                            <Trash2 className="w-5 h-5 text-red-400" />
                        </button>
                        </div>
                    ))}
                    </div>
                )}
            </div>
          </div>

          {/* 🆕 Sélection de catégorie */}
          {selectedModel && selectedProducts.length > 0 && (
            <div className="mt-8">
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 mb-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-yellow-400" />
                  3. Choisissez le type de vêtement
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {PRODUCT_CATEGORIES.map(cat => (
                    <button
                      key={cat.value}
                      onClick={() => setCategory(cat)}
                      className={`p-4 rounded-xl font-medium transition-all ${
                        category.value === cat.value
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white scale-105'
                          : 'bg-white/5 text-white hover:bg-white/10'
                      }`}
                    >
                      <div className="text-2xl mb-2">{cat.label.split(' ')[0]}</div>
                      <div className="text-xs">{cat.label.split(' ').slice(1).join(' ')}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur rounded-2xl p-6 mb-6 border border-white/10">
      <h3 className="text-white text-lg font-semibold mb-4 flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-purple-400" />
        4. Watermark (Optionnel)
      </h3>
      
      {/* Enable Watermark */}
      <label className="flex items-center gap-3 mb-6 cursor-pointer">
        <input
          type="checkbox"
          checked={watermarkSettings.enabled}
          onChange={(e) => setWatermarkSettings({...watermarkSettings, enabled: e.target.checked})}
          className="w-5 h-5 rounded accent-purple-500"
        />
        <span className="text-white font-medium">Ajouter un watermark sur les images</span>
      </label>

      {watermarkSettings.enabled && (
        <div className="space-y-4 pl-8 border-l-2 border-purple-500/30">
          
          {/* Type */}
          <div>
            <label className="text-white/80 text-sm mb-2 block">Type de watermark</label>
            <div className="flex gap-3">
              <button
                onClick={() => setWatermarkSettings({...watermarkSettings, type: 'text'})}
                className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                  watermarkSettings.type === 'text'
                    ? 'bg-purple-500 text-white'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                📝 Texte
              </button>
              <button
                onClick={() => setWatermarkSettings({...watermarkSettings, type: 'logo'})}
                className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                  watermarkSettings.type === 'logo'
                    ? 'bg-purple-500 text-white'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                🖼️ Logo
              </button>
            </div>
          </div>

          {/* Texte */}
          {watermarkSettings.type === 'text' && (
            <div>
              <label className="text-white/80 text-sm mb-2 block">Texte à afficher</label>
              <input
                type="text"
                value={watermarkSettings.text}
                onChange={(e) => setWatermarkSettings({...watermarkSettings, text: e.target.value})}
                placeholder="Ex: @MonHotel ou TeckiA Studio"
                className="w-full bg-white/10 text-white rounded-xl px-4 py-3 placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          )}

          {/* Logo Upload */}
          {watermarkSettings.type === 'logo' && (
            <div>
              <label className="text-white/80 text-sm mb-2 block">Logo (PNG transparent recommandé)</label>
              
              {watermarkSettings.logoPreview ? (
                <div className="relative bg-white/10 rounded-xl p-4">
                  <img 
                    src={watermarkSettings.logoPreview} 
                    alt="Logo preview" 
                    className="h-20 mx-auto object-contain"
                  />
                  <button
                    onClick={() => setWatermarkSettings({...watermarkSettings, logoFile: null, logoPreview: null})}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-lg hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="block cursor-pointer">
                  <div className="border-2 border-dashed border-white/30 rounded-xl p-8 text-center hover:border-purple-500 transition-all">
                    <Upload className="w-8 h-8 text-white/60 mx-auto mb-2" />
                    <p className="text-white/60 text-sm">Cliquez pour uploader votre logo</p>
                    <p className="text-white/40 text-xs mt-1">PNG, JPG (Max 5MB)</p>
                  </div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Preview
                        const preview = URL.createObjectURL(file);
                        setWatermarkSettings({...watermarkSettings, logoFile: file, logoPreview: preview});
                        
                        // Upload immédiatement
                        await uploadWatermarkLogo(file);
                      }
                    }}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          )}

          {/* Opacité */}
          <div>
            <label className="text-white/80 text-sm mb-2 block">
              Opacité: {Math.round(watermarkSettings.opacity * 100)}%
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={watermarkSettings.opacity}
              onChange={(e) => setWatermarkSettings({...watermarkSettings, opacity: parseFloat(e.target.value)})}
              className="w-full accent-purple-500"
            />
            <div className="flex justify-between text-white/40 text-xs mt-1">
              <span>Transparent</span>
              <span>Opaque</span>
            </div>
          </div>

        </div>
      )}
    </div>

    <div className="bg-gradient-to-br from-orange-500/10 to-pink-500/10 backdrop-blur rounded-2xl p-6 mb-6 border border-orange-500/30">
  <h3 className="text-white text-lg font-semibold mb-4 flex items-center gap-2">
    <Sparkles className="w-5 h-5 text-orange-400" />
    5. Instructions Spéciales pour l'IA (Optionnel)
  </h3>
  
  {/* Enable Additional Prompt */}
  <label className="flex items-center gap-3 mb-6 cursor-pointer">
    <input
      type="checkbox"
      checked={additionalPromptEnabled}
      onChange={(e) => setAdditionalPromptEnabled(e.target.checked)}
      className="w-5 h-5 rounded accent-orange-500"
    />
    <div>
      <span className="text-white font-medium">Ajouter des instructions personnalisées</span>
      <p className="text-white/60 text-xs mt-1">
        Personnalisez couleur, style, fond, ambiance, etc.
      </p>
    </div>
  </label>

  {additionalPromptEnabled && (
    <div className="space-y-4 pl-8 border-l-2 border-orange-500/30">
      <div>
        <label className="text-white/80 text-sm mb-2 block">
          Instructions pour TeckiA
        </label>
        <textarea
          value={additionalPrompt}
          onChange={(e) => setAdditionalPrompt(e.target.value)}
          placeholder="Ex: Robe rouge vif, fond blanc pur, éclairage naturel, style élégant"
          className="w-full h-28 px-4 py-3 rounded-xl bg-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all resize-none border border-white/20"
          maxLength={500}
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-white/40 text-xs">
            {additionalPrompt.length}/500 caractères
          </p>
        </div>
      </div>

      {/* Exemples */}
      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
        <p className="text-white/80 text-xs font-semibold mb-2">💡 Exemples d'instructions :</p>
        <ul className="space-y-1 text-white/60 text-xs list-disc list-inside">
          <li>Fond blanc pur, éclairage studio professionnel</li>
          <li>Couleur bleue marine, style élégant et moderne</li>
          <li>Ambiance plage, coucher de soleil, tons chauds</li>
          <li>Style vintage, tons sépia, atmosphère nostalgique</li>
          <li>Fond noir mat, éclairage dramatique, haute couture</li>
        </ul>
      </div>
      
    </div>
  )}
</div>

              {/* Generate Button */}
                <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 rounded-3xl p-8 border border-white/20">
                  {/* ✅ INDICATEUR LIMITE IMAGES */}
                  <div className="mb-4 px-4 py-3 rounded-xl bg-white/10 border border-white/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white/90 text-sm">Images générées</span>
                      <span className={`text-sm font-bold ${limits.images.can_upload ? 'text-green-300' : 'text-red-300'}`}>
                        {limits.images.current} / {limits.images.limit}
                      </span>
                    </div>
                    
                    <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${limits.images.can_upload ? 'bg-green-400' : 'bg-red-400'}`}
                        style={{ width: `${(limits.images.current / limits.images.limit) * 100}%` }}
                      />
                    </div>
                    {!limits.images.can_upload && (
                      <p className="text-red-300 text-xs mt-2">
                        ⚠️ Limite atteinte - Supprimez des images pour continuer
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <Sparkles className="w-8 h-8 text-white" />
                        <h3 className="text-2xl font-bold text-white">Prêt à générer</h3>
                        {additionalPromptEnabled && additionalPrompt && (
                          <div className="px-3 py-1 rounded-full bg-yellow-400/20 border border-yellow-400/30">
                            <span className="text-yellow-200 text-sm font-bold">★ Instructions IA</span>
                          </div>
                        )}
                      </div>
                      <p className="text-white/90 text-lg">
                        Catégorie: {category.label}
                      </p>
                      <p className="text-white/90 text-lg">
                        1 modèle × {selectedProducts.length} produits = {selectedProducts.length} images
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mt-3">
                        {watermarkSettings.enabled && (
                          <div className="px-3 py-1 rounded-full bg-white/10 text-white/80 text-xs flex items-center gap-1">
                            <Type className="w-3 h-3" />
                            Watermark
                          </div>
                        )}
                        {additionalPromptEnabled && additionalPrompt && (
                          <div className="px-3 py-1 rounded-full bg-orange-500/20 text-orange-200 text-xs flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            Instructions prioritaires
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                    onClick={generateBatch}
                    // 👇 C'EST ICI LA CLÉ : Si can_generate est FALSE, on désactive.
                    disabled={generating || limits.images.can_generate === false}
                    
                    className={`px-8 py-4 rounded-xl font-bold text-lg transition-all flex items-center gap-3 
                      ${(generating || limits.images.can_generate === false)
                        ? 'bg-gray-500 cursor-not-allowed opacity-50 text-white' // Gris si bloqué
                        : 'bg-white text-purple-600 hover:scale-105' // Blanc si ok
                      }`}
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin" />
                        Génération...
                      </>
                    ) : (limits.images.can_generate === false) ? ( // 👇 Affiche l'alerte si bloqué
                      <>
                        <AlertCircle className="w-6 h-6" />
                        Stockage plein ({limits.images.current}/{limits.images.limit})
                      </>
                    ) : (
                      <>
                        <Zap className="w-6 h-6" />
                        Générer maintenant
                      </>
                    )}
                  </button>
                  </div>
                </div>
            </div>
          )}
        </div>

        {/* Modal Delete Confirm */}
        {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999]">
                {/* ⬆️ z-[9999] au lieu de z-[100] */}
                <div className="bg-slate-900 rounded-2xl p-8 max-w-md border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">Confirmer la suppression</h3>
                <p className="text-white/70 mb-6">
                    {showDeleteConfirm.type === 'video' ? 'Cette vidéo' : 'Cet élément'} sera définitivement supprimé.
                </p>
                <div className="flex gap-4">
                    <button
                    onClick={() => setShowDeleteConfirm(null)}
                    className="flex-1 px-6 py-3 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-all"
                    >
                    Annuler
                    </button>
                    <button
                    onClick={() => deleteItem(showDeleteConfirm.type, showDeleteConfirm.id)}
                    className="flex-1 px-6 py-3 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-all"
                    >
                    Supprimer
                    </button>
                </div>
                </div>
            </div>
            )}

        {/* Loading Overlay */}
        {generating && (
  <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[150]">
    <div className="text-center max-w-md w-full px-4">
      <img 
        src="/TeckiA-logo.gif" 
        alt="Loading" 
        className="w-32 h-32 mx-auto mb-6 animate-pulse"
      />
      
      <div className="flex items-center gap-3 justify-center mb-4">
        <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
        <p className="text-white text-xl font-medium">Génération TeckiA...</p>
      </div>
      
      
      {/* Barre de progression */}
      <div className="w-full bg-white/10 rounded-full h-4 mb-3 overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 transition-all duration-500 ease-out"
          style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
        />
      </div>
      
      {/* Texte progression */}
      <p className="text-white text-lg font-bold mb-2">
        {progress.current} / {progress.total} images
      </p>
      
      <p className="text-white/60 text-sm">
        ~{Math.ceil((progress.total - progress.current) * 15 / 60)} min restantes
      </p>
      
      {/* Pourcentage */}
      <p className="text-purple-400 text-3xl font-bold mt-4">
        {progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}%
      </p>
    </div>
  </div>
)}

        {/* 🆕 Modal Alert personnalisée */}
        {alertModal.show && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[200]">
            <div className="bg-slate-900 rounded-2xl p-8 max-w-md border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                {alertModal.type === 'success' && <CheckCircle className="w-8 h-8 text-green-500" />}
                {alertModal.type === 'error' && <AlertCircle className="w-8 h-8 text-red-500" />}
                {alertModal.type === 'warning' && <AlertCircle className="w-8 h-8 text-yellow-500" />}
                {alertModal.type === 'info' && <AlertCircle className="w-8 h-8 text-blue-500" />}
                <h3 className="text-xl font-bold text-white">{alertModal.title}</h3>
              </div>
              <p className="text-white/70 mb-6 whitespace-pre-line">{alertModal.message}</p>
              <button
                onClick={closeAlert}
                className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold hover:scale-105 transition-all"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ========================================
  // VIEW: GENERATED IMAGES
  // ========================================
  if (view === 'generated') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 p-4 md:p-8">
        <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl mb-8">
          <div className="container mx-auto px-4 py-4 md:py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img 
                  src="/TeckiA-logo.gif" 
                  alt="TeckiA IA" 
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
                />
                <div>
                  <h1 className="text-base md:text-xl font-bold text-white">Images Générées</h1>
                  <p className="text-xs md:text-sm text-purple-200">{stats.total} images</p>
                
                </div>
                <button
                onClick={() => setView('credits')}
                className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20 hover:from-yellow-500/30 hover:to-orange-500/30 text-yellow-300 border border-yellow-500/30 transition-all hover:scale-105 text-sm md:text-base group"
              >
                <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                <span className="hidden sm:inline">Acheter Crédits</span>
                <span className="sm:hidden">Crédits</span>
              </button>
              </div>
            </div>
          </div>
        </header>


        <div className="max-w-7xl mx-auto">
                

          <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
            <button
              onClick={() => setView('manage')}
              className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium whitespace-nowrap transition-all"
            >
              📤 Gérer
            </button>
            <button
              onClick={() => setView('generated')}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold whitespace-nowrap"
            >
              📸 Images ({stats.total})
              {stats.limit_exceeded && (
                <span className="ml-2 px-2 py-1 rounded-full bg-red-500 text-xs">
                  ! {stats.displayed} affichées
                </span>
              )}
            </button>
            <button
              onClick={() => setView('videos')}
              className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium whitespace-nowrap transition-all"
            >
              🎬 Vidéos ({stats.totalVideos})
            </button>
            <button
              onClick={() => setView('stats')}
              className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium whitespace-nowrap transition-all"
            >
              📊 Stats
              
            </button>
          </div>
          <QuotasHeader />
{/* ⚠️ AVERTISSEMENT SI > 100 IMAGES */}
{stats.limit_exceeded && (
        <div className="mb-6 bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl p-6 border border-white/20">
          <div className="flex items-center gap-3 mb-3">
            <AlertCircle className="w-8 h-8 text-white" />
            <div>
              <h3 className="text-xl font-bold text-white">
                ⚠️ Limite d'images dépassée
              </h3>
              <p className="text-white/90 text-sm">
                Vous avez {stats.total} images au total, mais seules les 100 plus récentes sont affichées.
              </p>
            </div>
          </div>
          <div className="bg-white/10 rounded-xl p-4 border border-white/20">
            <p className="text-white text-sm mb-3">
              📊 <strong>Total réel :</strong> {stats.total} images<br/>
              👁️ <strong>Affichées :</strong> {stats.displayed} images (les plus récentes)<br/>
              🚫 <strong>Cachées :</strong> {stats.total - stats.displayed} images anciennes
            </p>
            <p className="text-white/80 text-sm">
              💡 <strong>Pour générer de nouvelles images :</strong><br/>
              Supprimez au moins {stats.total - 100 + 1} image(s) pour libérer de l'espace.
            </p>
          </div>
        </div>
      )}
          
          {/* 🆕 Barre de sélection pour Publication */}
          {selectedImagesForPublish.length > 0 && (
            <div className="mb-8 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Check className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      {selectedImagesForPublish.length} image(s) sélectionnée(s) pour publication
                    </h3>
                    <p className="text-white/70 text-sm">Prêt pour publication Facebook</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedImagesForPublish([])}
                  className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Mode de publication */}
              <div className="flex gap-4 mb-4">
                <button
                  onClick={() => setPublishMode('auto')}
                  className={`flex-1 px-6 py-4 rounded-xl font-bold transition-all ${
                    publishMode === 'auto'
                      ? 'bg-white text-blue-600'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Zap className="w-5 h-5" />
                    <span>Publication Automatique</span>
                  </div>
                  <p className="text-xs mt-1 opacity-70">Texte généré par IA</p>
                </button>
                <button
                  onClick={() => setPublishMode('manual')}
                  className={`flex-1 px-6 py-4 rounded-xl font-bold transition-all ${
                    publishMode === 'manual'
                      ? 'bg-white text-blue-600'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Send className="w-5 h-5" />
                    <span>Publication Manuelle</span>
                  </div>
                  <p className="text-xs mt-1 opacity-70">Votre propre texte</p>
                </button>
              </div>

              {/* Champ texte manuel */}
              {publishMode === 'manual' && (
                <textarea
                  value={publishCaption}
                  onChange={(e) => setPublishCaption(e.target.value)}
                  placeholder="Saisissez le texte de votre publication..."
                  className="w-full p-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 mb-4 min-h-[100px] focus:outline-none focus:ring-2 focus:ring-white/30"
                />
              )}

              {/* Bouton Publier */}
              <button
                onClick={publishSelectedImages}
                disabled={isPublishing || (publishMode === 'manual' && !publishCaption.trim())}
                className="w-full px-6 py-4 rounded-xl bg-white text-blue-600 font-bold hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Publication en cours...
                  </>
                ) : (
                  <>
                    <Facebook className="w-5 h-5" />
                    Publier sur Facebook
                  </>
                )}
              </button>
            </div>
          )}

          {/* 🆕 Barre de sélection pour Vidéos */}
          {selectedImagesForVideo.length > 0 && (
            <div className="mb-8 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6 border border-white/20">
                <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Video className="w-6 h-6 text-white" />
                    </div>
                    <div>
                    <h3 className="text-xl font-bold text-white">
                        {selectedImagesForVideo.length} image(s) sélectionnée(s) pour vidéo
                    </h3>
                    </div>
                </div>
                <button
                    onClick={() => setSelectedImagesForVideo([])}
                    className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-all"
                >
                    <X className="w-5 h-5" />
                </button>
                </div>

                {selectedImagesForVideo.length > 0 && (
  <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur rounded-2xl p-6 mb-6 border border-white/10">
    <h3 className="text-white text-lg font-semibold mb-4 flex items-center gap-2">
      <Sparkles className="w-5 h-5 text-blue-400" />
      Watermark sur les vidéos (Optionnel)
    </h3>
    
    <label className="flex items-center gap-3 mb-4 cursor-pointer">
      <input
        type="checkbox"
        checked={videoWatermarkEnabled}
        onChange={(e) => setVideoWatermarkEnabled(e.target.checked)}
        className="w-5 h-5 rounded accent-blue-500"
      />
      <span className="text-white font-medium">
        Ajouter un watermark texte sur chaque vidéo
      </span>
    </label>

    {videoWatermarkEnabled && (
      <div className="pl-8 border-l-2 border-blue-500/30">
        <p className="text-white/60 text-sm mb-3">
          Saisissez le texte qui apparaîtra au centre de chaque vidéo
        </p>
        
        <div className="mb-4">
          <label className="text-white/80 text-sm mb-2 block">Texte du watermark</label>
          <input
            type="text"
            value={videoWatermarkText}
            onChange={(e) => setVideoWatermarkText(e.target.value)}
            placeholder="Ex: @MonHotel, TeckiA Studio"
            className="w-full bg-white/10 text-white rounded-xl px-4 py-3 placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg font-medium"
            maxLength={50}
          />
          <p className="text-white/40 text-xs mt-2">
            {videoWatermarkText.length}/50 caractères
          </p>
        </div>
        
        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
          <p className="text-white/60 text-xs mb-2">Aperçu du texte :</p>
          <p className="text-white text-xl font-bold text-center py-3" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
            {videoWatermarkText || 'Votre texte ici'}
          </p>
        </div>
        
        <p className="text-white/40 text-xs mt-3">
          💡 Position : Centre • Opacité : 40% • Style : Texte blanc avec ombre
        </p>
      </div>
    )}
  </div>
)}

                {/* 🆕 SÉLECTEUR MODÈLE IA */}
                <div className="mb-4">
                <label className="text-white/80 text-sm font-medium mb-2 block">Modèle IA :</label>
                <div className="grid grid-cols-2 gap-3">
                    <button
                    onClick={() => setSelectedAIModel('wan')}
                    className={`px-4 py-3 rounded-xl font-bold transition-all ${
                        selectedAIModel === 'wan'
                        ? 'bg-white text-purple-600'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                    >
                    <div className="text-sm">TeckiA V12</div>
                    <div className="text-xs opacity-70">RAPIDE</div>
                    </button>
                    <button
                    onClick={() => setSelectedAIModel('hailua')}
                    className={`px-4 py-3 rounded-xl font-bold transition-all ${
                        selectedAIModel === 'hailua'
                        ? 'bg-white text-purple-600'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                    >
                    <div className="text-sm">TeckiA V15P</div>
                    <div className="text-xs opacity-70">PRO</div>
                    </button>
                </div>
                </div>

                <button
  onClick={generateVideosFromSelected}
  disabled={generatingVideos || !limits.videos.can_generate}
  className="w-full px-6 py-4 rounded-xl bg-white text-purple-600 font-bold hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
>
  {generatingVideos ? (
    <>
      <Loader2 className="w-5 h-5 animate-spin" />
      Génération vidéos...
    </>
  ) : !limits.videos.can_generate ? (
    <>
      <AlertCircle className="w-5 h-5" />
      Stockage vidéos plein ({limits.videos.current}/{limits.videos.limit})
    </>
  ) : (
    <>
      <Play className="w-5 h-5" />
      Générer {selectedImagesForVideo.length} vidéo(s)
    </>
  )}
</button>
            </div>
            )}

        
          {/* Grid images avec scroll */}
          {generatedImages.length > 0 ? (
            <div className="max-h-[600px] overflow-y-auto pr-2">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {generatedImages.map(img => (
                    <div key={img.$id} className="bg-white/5 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10 hover:scale-105 transition-all group relative">
                    {/* Checkboxes pour sélection */}
                    <div className="absolute top-2 left-2 z-10 flex gap-2">
                        {/* Checkbox Publication */}
                        <button
                        onClick={() => toggleImageSelection(img)}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                            selectedImagesForPublish.find(i => i.$id === img.$id)
                            ? 'bg-blue-600'
                            : 'bg-black/50 hover:bg-black/70'
                        }`}
                        title="Sélectionner pour publication"
                        >
                        {selectedImagesForPublish.find(i => i.$id === img.$id) ? (
                            <Facebook className="w-4 h-4 text-white" />
                        ) : (
                            <Facebook className="w-4 h-4 text-white/50" />
                        )}
                        </button>

                        {/* Checkbox Vidéo */}
                        <button
                        onClick={() => toggleImageForVideo(img)}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                            selectedImagesForVideo.find(i => i.$id === img.$id)
                            ? 'bg-white'
                            : 'bg-black/50 hover:bg-black/70'
                        }`}
                        title="Sélectionner pour vidéo"
                        >
                        {selectedImagesForVideo.find(i => i.$id === img.$id) ? (
                            <Video className="w-4 h-4 text-white" />
                        ) : (
                            <Video className="w-4 h-4 text-white/50" />
                        )}
                        </button>
                    </div>

                    <div className="relative">
                        <img
                        src={`${API_URL}${img.image_url}`}
                        alt="Generated"
                        className="w-full h-64 object-cover cursor-pointer"
                        onClick={() => setSelectedImage(img)}
                        />
                        {img.status === 'published' && (
                        <div className="absolute top-2 right-2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Publié
                        </div>
                        )}
                        <button
                        onClick={() => setSelectedImage(img)}
                        className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        >
                        <Eye className="w-8 h-8 text-white" />
                        </button>
                    </div>

                    {/* ✅ BOUTONS CORRIGÉS */}
                    <div className="p-4 space-y-2">
                        {/* Bouton Télécharger */}
                        <a
                        href={`${API_URL}${img.image_url}`}
                        download={`image_${img.$id}.png`}
                        className="w-full px-4 py-2 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold hover:scale-105 transition-all text-sm flex items-center justify-center gap-2"
                        >
                        <Download className="w-4 h-4" />
                        Télécharger
                        </a>
                        
                        {/* Bouton Supprimer */}
                        <button
                        onClick={() => setShowDeleteConfirm({ type: 'image', id: img.$id })}
                        className="w-full px-4 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-400 font-medium transition-all text-sm flex items-center justify-center gap-2"
                        >
                        <Trash2 className="w-4 h-4" />
                        Supprimer
                        </button>
                    </div>
                    </div>
                ))}
                </div>
            </div>
            ) : (
            <div className="text-center py-20">
                <ImageIcon className="w-20 h-20 text-white/20 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Aucune image</h3>
                <p className="text-white/60 mb-6">Générez vos premières images</p>
                <button
                onClick={() => setView('manage')}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold hover:scale-105 transition-all"
                >
                Commencer
                </button>
            </div>
            )}
        </div>

        {/* Modal Preview Image */}
{selectedImage && (
  <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-[100] p-4" onClick={() => setSelectedImage(null)}>
    <div className="max-w-7xl w-full h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
      {/* Header avec boutons */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white">Aperçu de l'image</h3>
        <div className="flex gap-3">
          {/* Bouton Télécharger */}
          <a
            href={`${API_URL}${selectedImage.image_url}`}
            download={`image_${selectedImage.$id}.png`}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold hover:scale-105 transition-all flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            Télécharger
          </a>
          
          {/* Bouton Fermer */}
          <button
            onClick={() => setSelectedImage(null)}
            className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all flex items-center gap-2"
          >
            <X className="w-5 h-5" />
            Fermer
          </button>
        </div>
      </div>
      
      {/* Image avec zoom */}
      <div className="flex-1 overflow-auto bg-black/50 rounded-2xl p-4">
        <img
          src={`${API_URL}${selectedImage.image_url}`}
          alt="Preview"
          className="w-full h-auto object-contain cursor-zoom-in hover:scale-105 transition-transform"
          style={{ maxHeight: 'none' }}
        />
      </div>
      
      {/* Infos image */}
      <div className="mt-4 bg-white/5 rounded-xl p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-white/60">Modèle</p>
            <p className="text-white font-medium">{selectedImage.model_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-white/60">Produit</p>
            <p className="text-white font-medium">{selectedImage.product_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-white/60">Statut</p>
            <p className="text-white font-medium">{selectedImage.status === 'published' ? '✅ Publié' : '📝 Brouillon'}</p>
          </div>
          <div>
            <p className="text-white/60">Date</p>
            <p className="text-white font-medium">{new Date(selectedImage.$createdAt).toLocaleDateString()}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

        {/* Modal Publish Single Image */}
        {showPublishModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-slate-900 rounded-2xl p-8 max-w-md w-full border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">Publier sur Facebook</h3>
              <textarea
                value={publishCaption}
                onChange={(e) => setPublishCaption(e.target.value)}
                placeholder="Légende (optionnel)"
                className="w-full p-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 mb-6 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowPublishModal(null);
                    setPublishCaption('');
                  }}
                  className="flex-1 px-6 py-3 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-all"
                >
                  Annuler
                </button>
                <button
                  onClick={publishToFacebook}
                  className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold hover:scale-105 transition-all flex items-center justify-center gap-2"
                >
                  <Facebook className="w-5 h-5" />
                  Publier
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Delete Confirm */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100]">
            <div className="bg-slate-900 rounded-2xl p-8 max-w-md border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">Confirmer la suppression</h3>
              <p className="text-white/70 mb-6">Cette action est irréversible.</p>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-6 py-3 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-all"
                >
                  Annuler
                </button>
                <button
                  onClick={() => deleteItem(showDeleteConfirm.type, showDeleteConfirm.id)}
                  className="flex-1 px-6 py-3 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-all"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading Overlay Vidéos */}
        {generatingVideos && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[150]">
            <div className="text-center">
              <img 
                src="/TeckiA-logo.gif" 
                alt="Loading" 
                className="w-32 h-32 mx-auto mb-4 animate-pulse"
              />
              <div className="flex items-center gap-2 justify-center mb-2">
                <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                <p className="text-white text-lg font-medium">TeckiA Génère . . .</p>
              </div>
              <p className="text-white/60">
                environ ~2 min par vidéo
              </p>
            </div>
          </div>
        )}

        {/* Modal Alert personnalisée */}
        {alertModal.show && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[200]">
            <div className="bg-slate-900 rounded-2xl p-8 max-w-md border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                {alertModal.type === 'success' && <CheckCircle className="w-8 h-8 text-green-500" />}
                {alertModal.type === 'error' && <AlertCircle className="w-8 h-8 text-red-500" />}
                {alertModal.type === 'warning' && <AlertCircle className="w-8 h-8 text-yellow-500" />}
                {alertModal.type === 'info' && <AlertCircle className="w-8 h-8 text-blue-500" />}
                <h3 className="text-xl font-bold text-white">{alertModal.title}</h3>
              </div>
              <p className="text-white/70 mb-6 whitespace-pre-line">{alertModal.message}</p>
              <button
                onClick={closeAlert}
                className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold hover:scale-105 transition-all"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ========================================
  // VIEW: VIDEOS
  // ========================================
  if (view === 'videos') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 p-4 md:p-8">
        <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl mb-8">
          <div className="container mx-auto px-4 py-4 md:py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img 
                  src="/TeckiA-logo.gif" 
                  alt="TeckiA IA" 
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
                />
                <div>
                  <h1 className="text-base md:text-xl font-bold text-white">Vidéos Générées</h1>
                  <p className="text-xs md:text-sm text-purple-200">{stats.totalVideos} vidéos</p>
                </div>
                <button
                onClick={() => setView('credits')}
                className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20 hover:from-yellow-500/30 hover:to-orange-500/30 text-yellow-300 border border-yellow-500/30 transition-all hover:scale-105 text-sm md:text-base group"
              >
                <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                <span className="hidden sm:inline">Acheter Crédits</span>
                <span className="sm:hidden">Crédits</span>
              </button>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto">
          <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
            <button
              onClick={() => setView('manage')}
              className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium whitespace-nowrap transition-all"
            >
              📤 Gérer
            </button>
            <button
              onClick={() => setView('generated')}
              className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium whitespace-nowrap transition-all"
            >
              📸 Images ({stats.total})
            </button>
            <button
              onClick={() => setView('videos')}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold whitespace-nowrap"
            >
              🎬 Vidéos ({stats.totalVideos})
            </button>
            <button
              onClick={() => setView('stats')}
              className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium whitespace-nowrap transition-all"
            >
              📊 Stats
            </button>
          </div>
          <QuotasHeader />
{/* Modal Delete Confirm */}
{showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100]">
            <div className="bg-slate-900 rounded-2xl p-8 max-w-md border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">Confirmer la suppression</h3>
              <p className="text-white/70 mb-6">Cette action est irréversible.</p>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-6 py-3 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-all"
                >
                  Annuler
                </button>
                <button
                  onClick={() => deleteItem(showDeleteConfirm.type, showDeleteConfirm.id)}
                  className="flex-1 px-6 py-3 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-all"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ⚠️ AVERTISSEMENT SI LIMITE VIDÉOS DÉPASSÉE */}
{stats.videos_limit_exceeded && (
  <div className="mb-6 bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl p-6 border border-white/20">
    <div className="flex items-center gap-3 mb-3">
      <AlertCircle className="w-8 h-8 text-white" />
      <div>
        <h3 className="text-xl font-bold text-white">
          ⚠️ Limite de vidéos dépassée
        </h3>
        <p className="text-white/90 text-sm">
          Vous avez {stats.totalVideos} vidéos au total, mais seules les {stats.displayedVideos} plus récentes sont affichées.
        </p>
      </div>
    </div>
    <div className="bg-white/10 rounded-xl p-4 border border-white/20">
      <p className="text-white text-sm mb-3">
        📊 <strong>Total réel :</strong> {stats.totalVideos} vidéos<br/>
        👁️ <strong>Affichées :</strong> {stats.displayedVideos} vidéos<br/>
        🚫 <strong>Cachées :</strong> {stats.totalVideos - stats.displayedVideos} vidéos
      </p>
      <p className="text-white/80 text-sm">
        💡 Supprimez au moins {stats.totalVideos - 50 + 1} vidéo(s) pour pouvoir générer de nouvelles vidéos.
      </p>
    </div>
  </div>
)}
          {videos.length > 0 ? (
            <div className="max-h-[600px] overflow-y-auto pr-2">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {videos.map(video => (
                    <div key={video.$id} className="bg-white/5 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10 hover:scale-105 transition-all relative">
                    {/* Checkbox sélection */}
                    <button
                    onClick={() => {
                      if (selectedVideosForMerge.find(v => v.$id === video.$id)) {
                        setSelectedVideosForMerge(selectedVideosForMerge.filter(v => v.$id !== video.$id));
                      } else {
                        setSelectedVideosForMerge([...selectedVideosForMerge, video]);
                      }
                    }}
                    className={`absolute top-2 left-2 z-10 w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                      selectedVideosForMerge.find(v => v.$id === video.$id)
                        ? 'bg-blue-600'
                        : 'bg-white/20 hover:bg-white/30 border-2 border-white/50'
                    }`}
                  >
                    {selectedVideosForMerge.find(v => v.$id === video.$id) ? (
                      <Check className="w-5 h-5 text-white" />
                    ) : (
                      <Video className="w-4 h-4 text-white/70" />
                    )}
                  </button>
                    <video
                        src={video.video_url.startsWith('http') ? video.video_url : `${API_URL}${video.video_url}`}
                        controls
                        className="w-full h-auto aspect-video object-contain bg-black"
                    />
                    <div className="p-4 space-y-2">
                        <a       
                        href={video.video_url.startsWith('http') ? video.video_url : `${API_URL}${video.video_url}`}
                        download={`video_${video.$id}.mp4`}
                        className="w-full px-4 py-2 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold hover:scale-105 transition-all text-sm flex items-center justify-center gap-2"
                        >
                        <Download className="w-4 h-4" />
                        Télécharger
                        </a>
                        
                        <button
                        onClick={() => setShowDeleteConfirm({ type: 'video', id: video.$id })}
                        className="w-full px-4 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-400 font-medium transition-all text-sm flex items-center justify-center gap-2"
                        >
                        <Trash2 className="w-4 h-4" />
                        Supprimer
                        </button>
                    </div>
                    </div>
                ))}
                </div>
            </div>
            ) : (
            <div className="text-center py-20">
                <Video className="w-20 h-20 text-white/20 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Aucune vidéo</h3>
                <p className="text-white/60 mb-6">Générez des vidéos depuis vos images</p>
                <button
                onClick={() => setView('generated')}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold hover:scale-105 transition-all"
                >
                Voir les images
                </button>
            </div>
            )}
        </div>

        {selectedVideosForMerge.length >= 1 && (
  <div className="mb-8 space-y-4">
    {/* Bouton Fusion (si 2+ vidéos) */}
    {selectedVideosForMerge.length >= 2 && (
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-6">
        <button
          onClick={mergeVideos}
          className="w-full px-6 py-4 rounded-xl bg-white text-blue-600 font-bold hover:scale-105 transition-all flex items-center justify-center gap-2"
        >
          <Video className="w-5 h-5" />
          Fusionner {selectedVideosForMerge.length} vidéos
        </button>
      </div>
    )}

    {/* Bouton Publication */}
    <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-6 border border-white/20">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Facebook className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">
              {selectedVideosForMerge.length} vidéo(s) sélectionnée(s)
            </h3>
            <p className="text-white/70 text-sm">Publier sur Facebook</p>
          </div>
        </div>
        <button
          onClick={() => setSelectedVideosForMerge([])}
          className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-all"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Mode publication */}
      <div className="flex gap-4 mb-4">
        <button
          onClick={() => setPublishMode('auto')}
          className={`flex-1 px-6 py-4 rounded-xl font-bold transition-all ${
            publishMode === 'auto'
              ? 'bg-white text-green-600'
              : 'bg-white/20 text-white hover:bg-white/30'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Zap className="w-5 h-5" />
            <span>Auto TeckiA</span>
          </div>
        </button>
        <button
          onClick={() => setPublishMode('manual')}
          className={`flex-1 px-6 py-4 rounded-xl font-bold transition-all ${
            publishMode === 'manual'
              ? 'bg-white text-green-600'
              : 'bg-white/20 text-white hover:bg-white/30'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Send className="w-5 h-5" />
            <span>Manuel</span>
          </div>
        </button>
      </div>

      {/* Texte manuel */}
      {publishMode === 'manual' && (
        <textarea
          value={publishCaption}
          onChange={(e) => setPublishCaption(e.target.value)}
          placeholder="Saisissez le texte de votre publication..."
          className="w-full p-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 mb-4 min-h-[100px] focus:outline-none focus:ring-2 focus:ring-white/30"
        />
      )}

      {/* Bouton Publier */}
      <button
        onClick={publishSelectedVideos}
        disabled={isPublishing || (publishMode === 'manual' && !publishCaption.trim())}
        className="w-full px-6 py-4 rounded-xl bg-white text-green-600 font-bold hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isPublishing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Publication...
          </>
        ) : (
          <>
            <Facebook className="w-5 h-5" />
            Publier sur Facebook
          </>
        )}
      </button>
    </div>
  </div>
)}

        {/* Modal Alert */}
        {alertModal.show && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[200]">
            <div className="bg-slate-900 rounded-2xl p-8 max-w-md border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                {alertModal.type === 'success' && <CheckCircle className="w-8 h-8 text-green-500" />}
                {alertModal.type === 'error' && <AlertCircle className="w-8 h-8 text-red-500" />}
                {alertModal.type === 'warning' && <AlertCircle className="w-8 h-8 text-yellow-500" />}
                {alertModal.type === 'info' && <AlertCircle className="w-8 h-8 text-blue-500" />}
                <h3 className="text-xl font-bold text-white">{alertModal.title}</h3>
              </div>
              <p className="text-white/70 mb-6 whitespace-pre-line">{alertModal.message}</p>
              <button
                onClick={closeAlert}
                className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold hover:scale-105 transition-all"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (view === 'credits') {
    return (
      <CreditsPurchase 
        onBack={() => {
          setView('manage'); // ou 'video_ads' / 'vtryon' / 'marketing_studio'
          // Recharger les subscriptions si nécessaire
        }} 
      />
    );
  }

  // ========================================
  // VIEW: STATS
  // ========================================
  if (view === 'stats') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 p-4 md:p-8">
        <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl mb-8">
          <div className="container mx-auto px-4 py-4 md:py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img 
                  src="/TeckiA-logo.gif" 
                  alt="TeckiA IA" 
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
                />
                <div>
                  <h1 className="text-base md:text-xl font-bold text-white">Statistiques</h1>
                  <p className="text-xs md:text-sm text-purple-200">Vue d'ensemble</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto">
          <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
            <button
              onClick={() => setView('manage')}
              className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium whitespace-nowrap transition-all"
            >
              📤 Gérer
            </button>
            <button
              onClick={() => setView('generated')}
              className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium whitespace-nowrap transition-all"
            >
              📸 Images ({stats.total})
            </button>
            <button
              onClick={() => setView('videos')}
              className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium whitespace-nowrap transition-all"
            >
              🎬 Vidéos ({stats.totalVideos})
            </button>
            <button
              onClick={() => setView('stats')}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold whitespace-nowrap"
            >
              📊 Stats
            </button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Total Images</p>
                  <p className="text-3xl font-bold text-white">{stats.total}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-600 to-emerald-600 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Images Publiées</p>
                  <p className="text-3xl font-bold text-white">{stats.published}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center">
                  <Video className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Total Vidéos</p>
                  <p className="text-3xl font-bold text-white">{stats.totalVideos}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Brouillons</p>
                  <p className="text-3xl font-bold text-white">{stats.draft}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                  <Upload className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Modèles</p>
                  <p className="text-3xl font-bold text-white">{models.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center">
                  <Upload className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Produits</p>
                  <p className="text-3xl font-bold text-white">{products.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Alert */}
        {alertModal.show && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[200]">
            <div className="bg-slate-900 rounded-2xl p-8 max-w-md border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                {alertModal.type === 'success' && <CheckCircle className="w-8 h-8 text-green-500" />}
                {alertModal.type === 'error' && <AlertCircle className="w-8 h-8 text-red-500" />}
                {alertModal.type === 'warning' && <AlertCircle className="w-8 h-8 text-yellow-500" />}
                {alertModal.type === 'info' && <AlertCircle className="w-8 h-8 text-blue-500" />}
                <h3 className="text-xl font-bold text-white">{alertModal.title}</h3>
              </div>
              <p className="text-white/70 mb-6 whitespace-pre-line">{alertModal.message}</p>
              <button
                onClick={closeAlert}
                className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold hover:scale-105 transition-all"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}